import * as React from 'react'
import TableCheckbox from '@/modules/drivers/shared/tableItems/TableCheckbox'
import TableTextRow from '@/modules/drivers/shared/tableItems/TableTextRow'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import type { ImiDeclarationItem } from '../../services/useImiDeclarationsService'
import Tooltip from '@/modules/drivers/shared/ToolTip'
import { LoaderIcon } from '@/app/features/icons/Loader'
import {
  faArrowLeftFromBracketSolid,
  faBallotCheckSolid,
  faEnvelopeSolid,
  FaIcon,
  faPrintSolid,
} from '@/lib/fa'

type ImiDeclarationRowProps = {
  item: ImiDeclarationItem
  isSelected: boolean
  isSelectionLocked: boolean
  busy: boolean
  canWithdraw: boolean
  isDraft: boolean
  statusLabel: string
  /** Position of this row in the visible list (0-based). */
  rowIndex: number
  onToggleSelect: (declarationId: string) => void
  onAction: (
    action: 'print' | 'email' | 'withdraw' | 'submit',
    item: ImiDeclarationItem,
  ) => void
}

// React 19 przyjmuje `ref` jako zwykly props - forwardRef niepotrzebny.
// Ref i data-index sa dla measureElement wirtualizatora.
const ImiDeclarationRow: React.FC<
  ImiDeclarationRowProps & {
    ref?: React.Ref<HTMLTableRowElement>
    'data-index'?: number
  }
> = ({
  ref,
  'data-index': dataIndex,
  item,
  isSelected,
  isSelectionLocked,
  busy,
  canWithdraw,
  isDraft,
  statusLabel,
  rowIndex,
  onToggleSelect,
  onAction,
}) => {
  const { t } = useLocalize()
  const [isHovered, setIsHovered] = React.useState(false)

  const handleMouseEnter = () => setIsHovered(true)
  const handleMouseLeave = () => setIsHovered(false)


  const rowZIndex = Math.max(40 - rowIndex, 1)

  return (
    <tr
      ref={ref}
      data-index={dataIndex}
      style={{ position: 'relative', zIndex: rowZIndex }}
      className={`notches-border--right tw-w-fit tw-border-b tw-bg-white hover:tw-bg-primary/10 ${
        isSelected ? 'tw-bg-primary/10 tw-font-medium' : ''
      }`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <TableCheckbox
        disabled={isSelectionLocked}
        checked={isSelected}
        onChange={() => onToggleSelect(item.declarationId)}
        isHovered={isHovered}
      />
      <TableTextRow pointer={false}>
        {item.driverLatinFullName || '-'}
      </TableTextRow>
      <TableTextRow pointer={false}>
        {item.declarationPostingCountry || '-'}
      </TableTextRow>
      <TableTextRow pointer={false}>
        {item.declarationStartDate || '-'}
      </TableTextRow>
      <TableTextRow pointer={false}>
        {item.declarationEndDate || '-'}
      </TableTextRow>
      <TableTextRow pointer={false}>{statusLabel}</TableTextRow>
      <TableTextRow pointer={false}>
        {busy ? (
          <div className="tw-flex tw-items-center tw-gap-2 tw-text-content-base tw-text-opacity-70">
            <LoaderIcon className="tw-h-4 tw-w-4 tw-animate-spin" />
            <span className="tw-text-xs">
              {t('common.loading') || 'Przetwarzanie…'}
            </span>
          </div>
        ) : (
          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
            <Tooltip
              text="Drukuj deklarację"
              onClick={() => onAction('print', item)}
              disabled={busy}
            >
              <FaIcon className="tw-text-primary" icon={faPrintSolid} />
            </Tooltip>
            <Tooltip
              text="Wyślij deklarację mailem"
              onClick={() => onAction('email', item)}
              disabled={busy}
            >
              <FaIcon className="tw-text-primary" icon={faEnvelopeSolid} />
            </Tooltip>
            {isDraft && (
              <Tooltip
                text={t('exportModule.submit-declaration') || 'Zatwierdź'}
                onClick={() => onAction('submit', item)}
                disabled={busy}
              >
                <FaIcon
                  className="tw-text-green-500"
                  icon={faBallotCheckSolid}
                />
              </Tooltip>
            )}
            {canWithdraw && !isDraft && (
              <Tooltip
                text={t('exportModule.withdraw')}
                onClick={() => onAction('withdraw', item)}
                disabled={busy}
              >
                <FaIcon
                  className="tw-text-danger"
                  icon={faArrowLeftFromBracketSolid}
                />
              </Tooltip>
            )}
          </div>
        )}
      </TableTextRow>
    </tr>
  )
}

// memo dziala tylko dopoki `onAction` i `onToggleSelect` sa stabilne - patrz
// handleSingleAction w IMIDeclarationsPage, ktore celowo zalezy od
// declarationsQuery.refetch, a nie od calego obiektu zapytania.
export default React.memo(ImiDeclarationRow)
